import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  RefreshCw, Activity, Shield, CheckCircle, AlertTriangle,
  Eye, Hash, BarChart3, AlertCircle,
} from 'lucide-react';
import * as api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import {
  BRAND, S, ACCENT, GovStyles, Card, StatCard,
  FilterSelect, PrimaryButton, GhostButton,
  btnBase, inputBase,
} from '../components/common/GovernanceDesignSystem';

/* ── Execution & Audit Page Styles ──────────────────────────────────────── */
const EAStyles = () => (
  <style>{`
    @keyframes eaSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes eaSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    .ea-row:hover { background: ${S[50]} !important; }
    .ea-card { animation: eaSlideUp 0.3s ease forwards; }
    .ea-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
  `}</style>
);

/* ── EA Card (accent top line) ──────────────────────────────────────────── */
const EACard = ({ children, accent, style = {} }) => (
  <div className="ea-card" style={{
    background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
    borderTop: accent ? `3px solid ${accent}` : `1px solid ${S[200]}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    transition: 'box-shadow 0.15s ease', ...style,
  }}>
    {children}
  </div>
);

/* ── EA Stat Card with Icon Badge ───────────────────────────────────────── */
const EAStatCard = ({ label, value, icon: Icon, accent, valueColor }) => (
  <EACard accent={accent?.icon || BRAND.primary} style={{ flex: 1, minWidth: 160, padding: '20px 22px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: accent?.bg || BRAND.light,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} color={accent?.icon || BRAND.primary} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: valueColor || S[900], letterSpacing: -0.3 }}>{value}</div>
        <div style={{ fontSize: 11, color: S[500], fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  </EACard>
);

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 };

// Utility functions (domain-specific — preserved)
const truncateId = (id, length = 8) => {
  if (!id) return 'N/A';
  return id.length > length ? `${id.substring(0, length)}...` : id;
};

const formatTime = (ms) => {
  if (!ms) return 'N/A';
  return `${ms}ms`;
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
};

const getPhaseColor = (phase) => {
  const colors = {
    COMPLETED: '#059669',
    BLOCKED: '#dc2626',
    FAILED: '#dc2626',
    EXECUTING: '#0891b2',
    PENDING: '#78716c',
    PRE_CHECK: '#d97706',
    POST_CHECK: '#d97706',
  };
  return colors[phase] || '#78716c';
};

const getAutonomyColor = (level) => {
  const colors = {
    AUTONOMOUS: '#059669',
    UNRESTRICTED: '#0891b2',
    LIMITED: '#d97706',
    RESTRICTED: '#dc2626',
  };
  return colors[level] || '#78716c';
};

// Main component
export default function GelEnvelopeDashboardPage({ agentScope = null }) {
  // agentScope: 'native' | 'external' | null (null = all agents, legacy behavior)
  const { user, session } = useAuth();
  // Derive tenant from session — use stable primitive to prevent infinite re-render loops
  const tenantId = session?.tenantId || null;
  const tenant = useMemo(() => tenantId ? { id: tenantId } : null, [tenantId]);
  const { addToast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Overview tab state
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Envelopes tab state
  const [envelopes, setEnvelopes] = useState([]);
  const [envelopesLoading, setEnvelopesLoading] = useState(false);
  const [selectedEnvelope, setSelectedEnvelope] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [searchExecutionId, setSearchExecutionId] = useState('');
  const [filterAgentId, setFilterAgentId] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const { page, pageSize: size, setPage: goToPage, setPageSize } = usePaginationState(10);

  // Agent lookup state — load once, use for filters, table display, and audit
  const [agentList, setAgentList] = useState([]);

  // Audit tab state
  const [auditAgentId, setAuditAgentId] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // Load metrics
  const loadMetrics = useCallback(async () => {
    if (!tenant?.id) return;
    setMetricsLoading(true);
    try {
      const result = await api.gelGetMetrics(tenant.id);
      setMetrics(result?.data || result);
    } catch (err) {
      addToast('Failed to load metrics: ' + err.message, 'error');
    }
    setMetricsLoading(false);
  }, [tenant?.id, addToast]);

  // Build agent ID → name map for display
  const agentMap = useMemo(() => {
    const m = {};
    agentList.forEach((a) => { m[a.id] = a.name || a.displayName || a.id; });
    return m;
  }, [agentList]);

  // Helper: resolve agent name from ID
  const agentName = useCallback((agentId) => agentMap[agentId] || truncateId(agentId), [agentMap]);

  // Full agent list (unfiltered) — used for scope identification
  const [fullAgentList, setFullAgentList] = useState([]);

  // Build set of external agentIds from full agent list
  const externalAgentIdSet = useMemo(() => {
    const set = new Set();
    fullAgentList.forEach(a => {
      if (a.isExternal) set.add(a.id);
    });
    return set;
  }, [fullAgentList]);

  // Load agents for filter dropdowns (runs once)
  const loadAgents = useCallback(async () => {
    try {
      const result = await api.getAgents({});
      // apiFetch wraps response as { ok, data } — unwrap it
      const raw = result?.data || result || [];
      const agents = Array.isArray(raw) ? raw : (raw.content || []);
      // Keep full list for scope identification
      setFullAgentList(agents);
      // Scope filtering: if agentScope is set, filter dropdown to matching agents
      let filtered = agents;
      if (agentScope === 'external') {
        filtered = agents.filter((a) => a.isExternal);
      } else if (agentScope === 'native') {
        filtered = agents.filter((a) => !a.isExternal);
      }
      setAgentList(filtered);
    } catch (err) {
      console.warn('Failed to load agents for filter:', err);
    }
  }, [agentScope]);

  // Load envelopes
  const loadEnvelopes = useCallback(async () => {
    if (!tenant?.id) return;
    setEnvelopesLoading(true);
    try {
      const result = await api.gelListEnvelopes({
        tenantId: tenant.id,
        agentId: filterAgentId || undefined,
        page,
        size,
      });
      // apiFetch wraps response as { ok, data }; inner data is Spring Page { content, totalElements, ... }
      const pageData = result?.data || result || {};
      let data = pageData.content || [];
      // Agent scope filter: match by externalRegistrationId OR by agentId in external set
      if (agentScope === 'native') {
        data = data.filter((e) => !e.externalRegistrationId && !externalAgentIdSet.has(e.agentId));
      } else if (agentScope === 'external') {
        data = data.filter((e) => !!e.externalRegistrationId || externalAgentIdSet.has(e.agentId));
      }
      if (searchExecutionId) {
        data = data.filter((e) => (e.executionId || '').includes(searchExecutionId));
      }
      if (filterPhase) {
        data = data.filter((e) => e.executionPhase === filterPhase);
      }
      setEnvelopes(data);
    } catch (err) {
      addToast('Failed to load envelopes: ' + err.message, 'error');
    }
    setEnvelopesLoading(false);
  }, [tenant?.id, page, size, searchExecutionId, filterAgentId, filterPhase, agentScope, externalAgentIdSet, addToast]);

  // Load envelope detail
  const loadEnvelopeDetail = useCallback(async (executionId) => {
    try {
      const result = await api.gelGetEnvelope(executionId);
      setSelectedEnvelope(result?.data || result);
      setDetailModalOpen(true);
    } catch (err) {
      addToast('Failed to load envelope details: ' + err.message, 'error');
    }
  }, [addToast]);

  // Verify hash chain
  const verifyHashChain = useCallback(async () => {
    if (!auditAgentId) {
      addToast('Please select an agent', 'warning');
      return;
    }
    setAuditLoading(true);
    try {
      const result = await api.gelVerifyHashChain(auditAgentId);
      setAuditResult(result?.data || result);
      addToast('Hash chain verification complete', 'success');
    } catch (err) {
      addToast('Verification failed: ' + err.message, 'error');
    }
    setAuditLoading(false);
  }, [auditAgentId, addToast]);

  // Initial load
  useEffect(() => {
    loadMetrics();
    loadAgents();
  }, [loadMetrics, loadAgents]);

  useEffect(() => {
    if (activeTab === 'envelopes') {
      loadEnvelopes();
    }
  }, [activeTab, loadEnvelopes]);

  // Memoized phase breakdown
  const phaseBreakdown = useMemo(() => {
    if (!metrics) return [];
    const phases = ['COMPLETED', 'BLOCKED', 'FAILED', 'EXECUTING', 'PENDING', 'PRE_CHECK', 'POST_CHECK'];
    return phases.map((phase) => ({
      phase,
      count: metrics[phase] || 0,
      percentage: metrics.total > 0 ? ((metrics[phase] || 0) / metrics.total) * 100 : 0,
    }));
  }, [metrics]);

  // Memoized recent activity
  const recentActivity = useMemo(() => {
    return envelopes.slice(0, 10);
  }, [envelopes]);

  /* ── Tab Config (Shadow Discovery / Trust Center style) ──────────────── */
  const TABS = [
    { key: 'overview', label: 'Overview', icon: BarChart3, desc: 'Execution metrics, phase distribution, and recent activity' },
    { key: 'envelopes', label: 'Envelopes', icon: Activity, desc: 'Search, filter, and inspect governance execution envelopes' },
    { key: 'audit', label: 'Audit', icon: Shield, desc: 'Hash chain integrity verification for governance envelopes' },
  ];

  const activeTabMeta = TABS.find(t => t.key === activeTab);

  return (
    <div style={{
      height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column',
      background: S[50], overflow: 'hidden',
    }}>
      <EAStyles />
      <GovStyles />

      {/* ── Sticky Header ──────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, background: '#fff', borderBottom: `1px solid ${S[200]}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '20px 32px 0' }}>
          <PageHeader
            title="Envelope Dashboard"
            subtitle="Execution metrics, protection envelopes, hash chain integrity"
            icon={Activity}
            scopeBadge={agentScope ? (agentScope === 'external' ? { label: 'ExteLets', accent: ACCENT.sky } : { label: 'Lets', accent: ACCENT.emerald }) : null}
          />

          {/* Tab Navigation (Trust Center / Shadow Discovery style) */}
          <div style={{ display: 'flex', gap: 0, padding: '0' }}>
            {TABS.map(({ key, label, icon: Icon, desc }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  className={isActive ? '' : 'ea-tab'}
                  onClick={() => setActiveTab(key)}
                  title={desc}
                  style={{
                    flex: 1, padding: '14px 16px',
                    borderBottom: isActive ? `3px solid ${BRAND.primary}` : '3px solid transparent',
                    background: 'transparent', border: 'none', borderBottomStyle: 'solid',
                    cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
                    color: isActive ? BRAND.primary : S[500],
                    transition: 'all 0.15s ease',
                    display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Active tab description bar ─── */}
      {activeTabMeta && (
        <div style={{
          padding: '8px 24px', background: BRAND.gradientSubtle,
          borderBottom: `1px solid ${S[150]}`,
          fontSize: 11, color: S[500], fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          <activeTabMeta.icon size={12} color={S[400]} />
          {activeTabMeta.desc}
        </div>
      )}

      {/* ── Scrollable Body ────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 32px 40px' }}>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <div>
          {/* Metrics Cards */}
          {metricsLoading ? (
            <EACard style={{ textAlign: 'center', color: S[500], padding: 24 }}>
              <RefreshCw size={24} style={{ animation: 'eaSpin 2s linear infinite' }} /> Loading metrics...
            </EACard>
          ) : metrics ? (
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              <EAStatCard
                label="Total Executions (24h)"
                value={metrics.total}
                icon={BarChart3}
                accent={ACCENT.slate}
              />
              <EAStatCard
                label="Completed"
                value={metrics.COMPLETED}
                icon={CheckCircle}
                accent={ACCENT.emerald}
              />
              <EAStatCard
                label="Blocked"
                value={metrics.BLOCKED}
                icon={AlertTriangle}
                accent={ACCENT.rose}
              />
              <EAStatCard
                label="Failed"
                value={metrics.FAILED}
                icon={AlertCircle}
                accent={ACCENT.rose}
              />
              <EAStatCard
                label="Avg Duration"
                value="~450ms"
                icon={RefreshCw}
                accent={ACCENT.slate}
              />
            </div>
          ) : null}

          {/* Phase Breakdown */}
          <EACard accent={BRAND.primary} style={{ padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: S[900], margin: '0 0 16px 0', letterSpacing: -0.3 }}>
              Execution Phase Distribution
            </h2>
            <div style={{ display: 'flex', gap: 2, height: 40, borderRadius: 6, overflow: 'hidden' }}>
              {phaseBreakdown.map((item) => (
                <div
                  key={item.phase}
                  style={{
                    flex: item.percentage,
                    background: getPhaseColor(item.phase),
                    minWidth: item.percentage > 5 ? 'auto' : 0,
                    opacity: 0.85,
                    transition: 'opacity 0.2s ease',
                  }}
                  title={`${item.phase}: ${item.count} (${item.percentage.toFixed(1)}%)`}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16, fontSize: 12 }}>
              {phaseBreakdown.map((item) => (
                <div key={item.phase} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      background: getPhaseColor(item.phase),
                    }}
                  />
                  <span style={{ color: S[500] }}>
                    {item.phase}: {item.count}
                  </span>
                </div>
              ))}
            </div>
          </EACard>

          {/* Recent Activity */}
          <EACard style={{ padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: S[900], margin: '0 0 16px 0', letterSpacing: -0.3 }}>
              Recent Activity
            </h2>
            {envelopesLoading ? (
              <div style={{ textAlign: 'center', color: S[500], padding: 20 }}>Loading...</div>
            ) : envelopes.length === 0 ? (
              <div style={{ textAlign: 'center', color: S[500], padding: 20 }}>No envelopes found</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {recentActivity.map((env) => (
                  <div
                    key={env.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 12,
                      borderRadius: 6,
                      border: `1px solid ${S[100]}`,
                      background: S[50],
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>
                        {truncateId(env.executionId)}
                      </div>
                      <div style={{ fontSize: 12, color: S[500], marginTop: 4 }}>
                        {agentName(env.agentId)} • {formatTime(env.totalDurationMs)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Badge status={env.executionPhase.toLowerCase()}>{env.executionPhase}</Badge>
                      <div style={{ fontSize: 12, color: S[400] }}>
                        {formatRelativeTime(env.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </EACard>
        </div>
      )}

      {/* TAB 2: ENVELOPES */}
      {activeTab === 'envelopes' && (
        <div>
          {/* Filter Bar */}
          <EACard style={{ padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: S[900], margin: '0 0 16px 0', letterSpacing: -0.3 }}>
              Search &amp; Filter
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr auto', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                  Agent
                </label>
                <select
                  value={filterAgentId}
                  onChange={(e) => setFilterAgentId(e.target.value)}
                  style={{ ...inputBase, width: '100%' }}
                >
                  <option value="">All Agents</option>
                  {agentList.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                  Phase
                </label>
                <select
                  value={filterPhase}
                  onChange={(e) => setFilterPhase(e.target.value)}
                  style={{ ...inputBase, width: '100%' }}
                >
                  <option value="">All Phases</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="BLOCKED">Blocked</option>
                  <option value="FAILED">Failed</option>
                  <option value="EXECUTING">Executing</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                  Execution ID
                </label>
                <input
                  type="text"
                  placeholder="Search by ID..."
                  value={searchExecutionId}
                  onChange={(e) => setSearchExecutionId(e.target.value)}
                  style={inputBase}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <GhostButton onClick={loadEnvelopes} icon={RefreshCw}>
                  Refresh
                </GhostButton>
              </div>
            </div>
          </EACard>

          {/* Envelopes Table */}
          <EACard style={{ padding: 24, marginBottom: 20 }}>
            {envelopesLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: S[500] }}>
                <RefreshCw size={24} style={{ animation: 'eaSpin 2s linear infinite' }} /> Loading envelopes...
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: S[50] }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Execution ID</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Agent</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Phase</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Stop Reason</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Trust Score</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Autonomy</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Duration</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Platform</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Created</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${S[200]}` }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {envelopes.map((env) => (
                        <tr key={env.id} className="ea-row">
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <code style={{ fontSize: 11, color: BRAND.primary, fontFamily: 'monospace' }}>
                              {truncateId(env.executionId)}
                            </code>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span style={{ fontSize: 12, fontWeight: 500 }} title={env.agentId}>
                              {agentName(env.agentId)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <Badge status={env.executionPhase.toLowerCase()}>{env.executionPhase}</Badge>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span style={{ fontSize: 12, color: S[500] }}>
                              {env.stopReason || '—'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>
                              {(env.trustScoreAtExecution || 0).toFixed(0)}%
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: getAutonomyColor(env.autonomyLevelAtExecution),
                              }}
                            >
                              {env.autonomyLevelAtExecution}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            {formatTime(env.totalDurationMs)}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span style={{ fontSize: 12, color: S[500] }}>
                              {env.platformType}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <span style={{ fontSize: 12, color: S[400] }}>
                              {formatRelativeTime(env.createdAt)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                            <button
                              onClick={() => loadEnvelopeDetail(env.executionId)}
                              className="gov-action-btn"
                              style={{ ...btnBase, padding: '4px 8px', background: 'transparent', color: S[600], border: `1px solid ${S[200]}`, borderRadius: 6 }}
                              title="View details"
                            >
                              <Eye size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {envelopes.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, color: S[500] }}>
                    No envelopes found
                  </div>
                )}

                {envelopes.length > 0 && (
                  <Pagination
                    totalItems={envelopes.length}
                    page={page}
                    pageSize={size}
                    onPageChange={goToPage}
                    onPageSizeChange={setPageSize}
                  />
                )}
              </>
            )}
          </EACard>
        </div>
      )}

      {/* TAB 3: AUDIT */}
      {activeTab === 'audit' && (
        <div>
          {/* Hash Chain Verifier */}
          <EACard style={{ padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: S[900], margin: '0 0 16px 0', letterSpacing: -0.3 }}>
              Hash Chain Verifier
            </h2>
            <p style={{ fontSize: 13, color: S[500], margin: '0 0 24px 0' }}>
              Verify the integrity of protection envelopes for an agent
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 8 }}>
                  Agent
                </label>
                <select
                  value={auditAgentId}
                  onChange={(e) => setAuditAgentId(e.target.value)}
                  style={{ ...inputBase, width: '100%' }}
                >
                  <option value="">Select an agent...</option>
                  {agentList.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <PrimaryButton
                  onClick={verifyHashChain}
                  disabled={auditLoading || !auditAgentId}
                  icon={Hash}
                >
                  {auditLoading ? 'Verifying...' : 'Verify Chain'}
                </PrimaryButton>
              </div>
            </div>

            {/* Verification Result */}
            {auditResult && (
              <div
                style={{
                  borderRadius: 8,
                  padding: 16,
                  border: `2px solid ${auditResult.valid ? ACCENT.emerald.icon : ACCENT.rose.text}`,
                  background: auditResult.valid ? ACCENT.emerald.bg : ACCENT.rose.bg,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  {auditResult.valid ? (
                    <CheckCircle size={24} color={ACCENT.emerald.icon} />
                  ) : (
                    <AlertTriangle size={24} color={ACCENT.rose.text} />
                  )}
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: auditResult.valid ? ACCENT.emerald.icon : ACCENT.rose.text,
                      }}
                    >
                      {auditResult.valid ? 'Chain Valid' : 'Chain Invalid'}
                    </div>
                    <div style={{ fontSize: 12, color: S[500] }}>
                      {auditResult.envelopesChecked} envelopes verified
                    </div>
                  </div>
                </div>

                {auditResult.chainBreaks && auditResult.chainBreaks.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: ACCENT.rose.text, marginBottom: 8 }}>
                      Chain Breaks Detected:
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${ACCENT.rose.border}` }}>
                            <th style={{ padding: 8, textAlign: 'left' }}>Index</th>
                            <th style={{ padding: 8, textAlign: 'left' }}>Execution ID</th>
                            <th style={{ padding: 8, textAlign: 'left' }}>Expected Hash</th>
                            <th style={{ padding: 8, textAlign: 'left' }}>Actual Hash</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditResult.chainBreaks.map((br, idx) => (
                            <tr key={idx} style={{ borderBottom: `1px solid ${ACCENT.rose.border}` }}>
                              <td style={{ padding: 8 }}>{idx}</td>
                              <td style={{ padding: 8 }}>{truncateId(br.executionId)}</td>
                              <td style={{ padding: 8, fontFamily: 'monospace' }}>
                                {truncateId(br.expectedHash, 12)}
                              </td>
                              <td style={{ padding: 8, fontFamily: 'monospace' }}>
                                {truncateId(br.actualHash, 12)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </EACard>

          {/* Integrity Status Summary */}
          {auditResult && (
            <EACard
              accent={auditResult.valid ? ACCENT.emerald.icon : ACCENT.rose.text}
              style={{
                padding: 24,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 24,
                textAlign: 'center',
              }}
            >
              {auditResult.valid ? (
                <>
                  <CheckCircle size={64} color={ACCENT.emerald.icon} />
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: ACCENT.emerald.icon, margin: 0 }}>
                      Integrity Verified
                    </h3>
                    <p style={{ fontSize: 12, color: S[500], marginTop: 4 }}>
                      All envelopes passed hash chain verification
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle size={64} color={ACCENT.rose.text} />
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: ACCENT.rose.text, margin: 0 }}>
                      Integrity Compromised
                    </h3>
                    <p style={{ fontSize: 12, color: S[500], marginTop: 4 }}>
                      Chain breaks detected. Immediate action required.
                    </p>
                  </div>
                </>
              )}
            </EACard>
          )}
        </div>
      )}

        </div>{/* end maxWidth wrapper */}
      </div>{/* end scrollable body */}

      {/* Envelope Detail Modal */}
      <Modal
        isOpen={detailModalOpen}
        title={selectedEnvelope ? `Envelope: ${truncateId(selectedEnvelope.executionId)}` : 'Envelope Details'}
        onClose={() => setDetailModalOpen(false)}
      >
        {selectedEnvelope && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Execution Info */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Execution Info
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Execution ID</div>
                  <code style={{ color: BRAND.primary, fontFamily: 'monospace' }}>
                    {selectedEnvelope.executionId}
                  </code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Phase</div>
                  <Badge status={selectedEnvelope.executionPhase.toLowerCase()}>
                    {selectedEnvelope.executionPhase}
                  </Badge>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Agent ID</div>
                  <code style={{ fontFamily: 'monospace' }}>{truncateId(selectedEnvelope.agentId)}</code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>User ID</div>
                  <code style={{ fontFamily: 'monospace' }}>
                    {selectedEnvelope.userId ? truncateId(selectedEnvelope.userId) : 'N/A'}
                  </code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Stop Reason</div>
                  <span>{selectedEnvelope.stopReason || 'N/A'}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Created</div>
                  <span>{new Date(selectedEnvelope.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Governance */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Protection
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Trust Score</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {(selectedEnvelope.trustScoreAtExecution || 0).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Autonomy Level</div>
                  <span
                    style={{
                      color: getAutonomyColor(selectedEnvelope.autonomyLevelAtExecution),
                      fontWeight: 600,
                    }}
                  >
                    {selectedEnvelope.autonomyLevelAtExecution}
                  </span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>LLM Policy Enforced</div>
                  <span>{selectedEnvelope.llmPolicyEnforced ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Tool Count</div>
                  <span>{selectedEnvelope.toolCount}</span>
                </div>
              </div>
            </div>

            {/* Platform */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Platform
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Type</div>
                  <span>{selectedEnvelope.platformType}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Status Code</div>
                  <span>{selectedEnvelope.platformStatusCode}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Response Time</div>
                  <span>{formatTime(selectedEnvelope.platformResponseTimeMs)}</span>
                </div>
              </div>
            </div>

            {/* Timing */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Timing
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Pre-Check Duration</div>
                  <span>{formatTime(selectedEnvelope.preCheckDurationMs)}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Execution Duration</div>
                  <span>{formatTime(selectedEnvelope.executionDurationMs)}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Post-Check Duration</div>
                  <span>{formatTime(selectedEnvelope.postCheckDurationMs)}</span>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Total Duration</div>
                  <span style={{ fontWeight: 700 }}>{formatTime(selectedEnvelope.totalDurationMs)}</span>
                </div>
              </div>
            </div>

            {/* Hash Chain */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Hash Chain
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Input Hash</div>
                  <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 11 }}>
                    {selectedEnvelope.rawInputHash}
                  </code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Output Hash</div>
                  <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 11 }}>
                    {selectedEnvelope.rawOutputHash}
                  </code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Previous Envelope Hash</div>
                  <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 11 }}>
                    {selectedEnvelope.previousEnvelopeHash}
                  </code>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600 }}>Envelope Hash</div>
                  <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 11 }}>
                    {selectedEnvelope.envelopeHash}
                  </code>
                </div>
              </div>
            </div>

            {/* Payloads */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: S[900], marginBottom: 12 }}>
                Payloads
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <div style={{ color: S[500], fontWeight: 600, marginBottom: 6 }}>Input Payload</div>
                  <pre
                    style={{
                      background: S[50],
                      padding: 8,
                      borderRadius: 4,
                      fontSize: 11,
                      overflow: 'auto',
                      maxHeight: 150,
                    }}
                  >
                    {JSON.stringify(selectedEnvelope.inputPayload, null, 2)}
                  </pre>
                </div>
                <div>
                  <div style={{ color: S[500], fontWeight: 600, marginBottom: 6 }}>Output Payload</div>
                  <pre
                    style={{
                      background: S[50],
                      padding: 8,
                      borderRadius: 4,
                      fontSize: 11,
                      overflow: 'auto',
                      maxHeight: 150,
                    }}
                  >
                    {JSON.stringify(selectedEnvelope.outputPayload, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
